import React, {
    FormEvent,
    ReactElement, useState,
} from "react";
import { Form } from "react-bootstrap";
import { FiTrash2 } from "react-icons/fi";
import { FaRegClone } from "react-icons/fa";
import { BiExpandAlt } from "react-icons/bi";
import { removeTypename } from "../../../../api";
import { Loading } from "../../../../components";
import Confirm from "../../../../components/confirm/Confirm";
import BooleanSelect from "../../../../components/pickers/boolean/BooleanSelect";
import DeflectionSource from "../../../../components/pickers/deflection-sources/DeflectionSource";
import ExternalForms from "../../../../components/pickers/external-forms/ExternalForms";
import UserGroups from "../../../../components/pickers/user-groups/UserGroups";
import WorkflowDisplay, { createWorklowStep } from "../../../../components/workflow/Workflow";
import usePortal from "../../../../contexts/PortalContext";
import useRouting, { useParams } from "../../../../contexts/Routing";
import useCurrentSession from "../../../../contexts/session";
import {
    FormField, FormFieldDescriptorInput,
    FormType,
    GetFormDataDocument, GetFormDocument, GetRequestTypesDocument, JobFunction, ListRequestTypesDocument,
    Permission,
    useCloneFormMutation, useDeleteFormMutation, useGetExternalFormFieldsQuery, useGetFormDataQuery,
    useGetFormQuery, useSaveFormMutation, useSaveWorkflowMutation, WorkflowStep,
} from "../../../../__generated__/api";
import { ErrorRemote } from "../../../error/Error";
import FormDesigner from "./FormDesigner";
import createFormFields from "../../../../components/workflow/WorkflowFields";
import useDirtyTracker from "../../../../components/dirty-tracker/DirtyTracker";
import TagList from "../../../../components/tag-list/TagList";
import Select from "../../../../components/select/Select";
import IconUploader from "../../../../components/pickers/icon-uploader/IconUploader";
import useArticleContext from "../../../../contexts/HelpContext";
import Editor from "../../../../components/editor/Editor";

export const JOB_FUNCTION_NAME = {
    [JobFunction.CustomerSupport]: "Customer Support",
    [JobFunction.CustomerSuccess]: "Customer Success",
    [JobFunction.Engineering]: "Engineering",
    [JobFunction.Finance]: "Finance",
    [JobFunction.HumanResources]: "Human Resources (HR)",
    [JobFunction.InformationTechnology]: "Information Technology (IT)",
    [JobFunction.Legal]: "Legal",
    [JobFunction.Partnerships]: "Partnerships",
    [JobFunction.Product]: "Product",
    [JobFunction.RevenueOperations]: "Revenue Operations",
    [JobFunction.Sales]: "Sales",
    [JobFunction.Founder]: "Executive",
    [JobFunction.Security]: "Security",
    [JobFunction.Other]: "Other",
    [JobFunction.OtherInternal]: "Other",

};

export default function RequestTypes(): ReactElement {
    useArticleContext("urn:fq:article:173ca80539904f9c98277f0c589d2d6e");

    const { Paths, historyPush } = useRouting();
    const { id: portalId } = usePortal();
    const { permissions } = useCurrentSession();

    const [isExpandedAutomation, setExpandAutomations] = useState(false);
    const { id } = useParams(Paths.ADMINISTRATOR_SETTING_ID);
    const userSession = useCurrentSession();

    const isNew = id === "new" || id === "template";

    const {
        data, error, loading: isLoading,
    } = useGetFormQuery({
        skip: id == null,
        fetchPolicy: "no-cache",
        variables: {
            id: isNew ? "urn:fq:form:new" : id,
            hasId: !isNew,
        },
    });

    const isTemplate = permissions.has(Permission.SuperAdmin) && (id === "template" || data?.form?.type === FormType.Template);

    const [externalFormId, setExternalFormId] = useState(() => data?.form?.externalFormId);

    const { data: formData, loading: isDataLoading, refetch } = useGetFormDataQuery({
        variables: {
            shouldIncludeTemplateFields: isTemplate,
        },
    });
    const { data: externalData } = useGetExternalFormFieldsQuery({
        variables: {
            id: externalFormId,
        },
        skip: externalFormId == null,
    });
    const [saveForm] = useSaveFormMutation();
    const [saveWorkflow] = useSaveWorkflowMutation();
    const [cloneForm] = useCloneFormMutation();
    const [deleteForm] = useDeleteFormMutation();

    const [name, setName] = useState(() => data?.form?.name ?? "");
    const [description, setDescription] = useState(() => data?.form?.description ?? "");
    const [tags, setTags] = useState(() => data?.form?.tags ?? []);
    const [jobFunctions, setJobFunctions] = useState(() => data?.form?.jobFunctions ?? []);
    const [iconUrl, setIconUrl] = useState(() => data?.form?.iconUrl);
    const [shouldCreateOnOnboarding, setShouldCreateOnOnboarding] = useState(() => data?.form?.shouldCreateOnOnboarding);

    const [isCreatePrivate, setIsCreatePrivate] = useState(() => data?.form?.isCreatePrivate ?? false);
    const [deflectionIdentity, setDeflectionIdentity] = useState(() => (data?.form?.deflection?.identity
        ?? (data?.form?.deflection?.isEnabled ? portalId : undefined)));
    const [workflow, setWorkflow] = useState<WorkflowStep[]>(() => data?.form?.workflow?.steps as WorkflowStep[] ?? []);

    const [userGroupIds, setUserGroupIds] = useState(() => data?.form?.userGroupIds as string[]
            ?? userSession.user?.userGroups?.toIdList("id")
            ?? []);

    const [fields, setFields] = useState<FormFieldDescriptorInput[]>(
        () => data?.form?.fields.map(field => removeTypename(field)) ?? [],
    );

    const [displayDelete, setDisplayDelete] = useState(false);
    const [displayClone, setDisplayClone] = useState(false);

    const dirtyTracker = useDirtyTracker({
        finishedSavingMessage: `Finished saving "${name}".`,
    });

    dirtyTracker.useWatchProperty(name, data?.form?.name || "");
    dirtyTracker.useWatchProperty(description, data?.form?.description || "");
    dirtyTracker.useWatchProperty(isCreatePrivate, data?.form?.isCreatePrivate ?? false);
    dirtyTracker.useWatchProperty(externalFormId, data?.form?.externalFormId);
    dirtyTracker.useWatchProperty(deflectionIdentity, data?.form?.deflection?.identity
        ?? (data?.form?.deflection?.isEnabled ? portalId : undefined));

    dirtyTracker.useLoad(() => {
        setName(data?.form?.name ?? "");
        setDescription(data?.form?.description ?? "");
        setTags(data?.form?.tags ?? []);
        setJobFunctions(data?.form?.jobFunctions ?? []);
        setIconUrl(data?.form?.iconUrl);
        setShouldCreateOnOnboarding(data?.form?.shouldCreateOnOnboarding);

        setFields(data?.form?.fields.map(field => removeTypename(field)) ?? []);
        setUserGroupIds(data?.form?.userGroupIds ?? []);
        setIsCreatePrivate(data?.form?.isCreatePrivate ?? false);
        setDeflectionIdentity(data?.form?.deflection?.identity
            ?? (data?.form?.deflection?.isEnabled ? portalId : undefined));
        setWorkflow(data?.form?.workflow?.steps as WorkflowStep[] ?? []);
        setExternalFormId(data?.form?.externalFormId);
    }, [data?.form]);

    if (isLoading || isDataLoading) {
        return <Loading />;
    }
    if (error) {
        return <ErrorRemote error={error} />;
    }

    dirtyTracker.onSave((event, onSave) => {
        event.preventDefault();
        (async () => {
            const workflowVersion = !workflow ? undefined : await saveWorkflow({
                variables: {
                    id: data?.form?.workflow?.id,
                    workflow: {
                        name: "Auto generated",
                        description: "Auto generated for form",
                        steps: workflow.map(createWorklowStep),
                        iconUrl,
                        isTemplate,
                    },
                },
            });

            const savedForm = await saveForm({
                refetchQueries: [
                    { query: GetFormDataDocument },
                    { query: ListRequestTypesDocument },
                ],
                variables: {
                    id: !isNew ? id : undefined,
                    form: {
                        fields: fields.map(field => ({
                            ...field,
                            __typename: undefined,
                        })),
                        type: isTemplate ? FormType.Template : FormType.Default,
                        name,
                        description,
                        isCreatePrivate,
                        userGroupIds,
                        tags,
                        iconUrl,
                        shouldCreateOnOnboarding,
                        jobFunctions,
                        workflowId: workflowVersion?.data?.saveWorkflow?.id,
                        deflection: {
                            isEnabled: deflectionIdentity != null,
                            identity: deflectionIdentity,
                        },
                        externalFormId,

                    },
                },
            });
            if (isNew) {
                historyPush(Paths.ADMINISTRATOR_SETTING_ID, {
                    setting: "request-forms",
                    id: savedForm.data?.saveForm.id,
                });
            }
            onSave();
        })();
    });

    const onDelete = async () => {
        await deleteForm({
            refetchQueries: [
                { query: GetFormDataDocument },
            ],
            variables: {
                id,
            },
        });
        if (isTemplate) {
            historyPush(Paths.SITE_ADMINISTRATOR_SETTING, {
                setting: "form-templates",
            });
        } else {
            historyPush(Paths.ADMINISTRATOR_SETTING, {
                setting: "request-types",
            });
        }
    };

    const onClone = async () => {
        const cloned = await cloneForm({
            refetchQueries: [
                { query: GetFormDataDocument },
                { query: GetFormDocument, variables: { id } },
                { query: GetRequestTypesDocument },
            ],
            variables: {
                id,
            },
        });
        setDisplayClone(false);
        historyPush(Paths.ADMINISTRATOR_SETTING_ID, {
            setting: "request-forms",
            id: cloned.data?.cloneForm.id,
        });
    };

    const fieldMap = formData?.formFields.toIdMap("id") ?? {};

    const formFields = (externalData?.externalFormFields as FormField[]
        ?? fields.mapFilter(row => (row.fieldId ? fieldMap[row.fieldId] : undefined)));

    return (
        <>
            <Confirm confirmText="Delete" onConfirm={onDelete} shouldShow={displayDelete} onHide={() => setDisplayDelete(false)}>
                <div>
                    Are you sure you want to delete the request type:
                    {" "}
                    <div className="mt-2 p-2 rounded alert alert-danger">
                        {name}
                    </div>

                </div>
            </Confirm>

            <Confirm
                confirmText={!dirtyTracker.isDirty ? "Clone" : undefined}
                onConfirm={!dirtyTracker.isDirty ? onClone : undefined}
                shouldShow={displayClone}
                onHide={() => setDisplayClone(false)}
            >
                <div>
                    {dirtyTracker.isDirty ? (
                        <>
                            To clone this request type, please save or undo changes then come back to this
                            form and click Clone.
                        </>
                    ) : (
                        <>
                            <h4>
                                Clone
                                {" "}
                                {name}
                            </h4>
                            By pressing clone, this will make a copy of this request type.
                        </>
                    )}

                </div>
            </Confirm>
            <div>
                <div className="mb-4 d-flex">
                    <div className="col border-end me-3 pe-3">
                        <form className="border p-3 rounded mb-4 bg-white">

                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bolder">Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Name"
                                    required
                                    onChange={evt => setName(evt.target.value)}
                                    value={name}
                                />
                                <Form.Text>
                                    The name of the request type displayed at the top and in the request type selection.
                                </Form.Text>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bolder">
                                    Description
                                    {" "}
                                    <small className="text-light">(Optional)</small>
                                </Form.Label>
                                <Editor
                                    className="col border border-gray rounded"
                                    onChange={setDescription}
                                    text={description}
                                    placeholder="This request type is used for ..."
                                />

                                <Form.Text>
                                    The optional description is to help you understand where this request
                                    type is used. It will never be displayed to the end user.
                                </Form.Text>
                            </Form.Group>
                            {isTemplate ? (
                                <>

                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-bolder">
                                            Icon
                                        </Form.Label>
                                        <IconUploader
                                            imageClassName="sm"
                                            value={iconUrl as string | undefined}
                                            onChange={(value => {
                                                setIconUrl(value);
                                                dirtyTracker.dirty();
                                            })}
                                        />
                                        <Form.Text>
                                            What tags should be applied to this template?
                                        </Form.Text>
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-bolder">
                                            Create when onboarding
                                        </Form.Label>
                                        <BooleanSelect
                                            value={shouldCreateOnOnboarding as boolean | undefined}
                                            onChange={(value => {
                                                setShouldCreateOnOnboarding(value);
                                                dirtyTracker.dirty();
                                            })}
                                        />
                                        <Form.Text>
                                            Should this form be automatically created when setting up the user for the first time.
                                        </Form.Text>
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-bolder">
                                            Tags
                                        </Form.Label>
                                        <TagList
                                            onChange={values => {
                                                setTags(values);
                                                dirtyTracker.dirty();
                                            }}
                                            value={tags}
                                            canNotHaveDuplicates
                                            shouldSort
                                        />
                                        <Form.Text>
                                            What tags should be applied to this template?
                                        </Form.Text>
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-bolder">
                                            Job Functions
                                        </Form.Label>
                                        <Select
                                            isMulti
                                            options={Object.entries(JOB_FUNCTION_NAME).map(([value, label]) => ({
                                                label,
                                                value,
                                            }))}
                                            onChange={value => {
                                                setJobFunctions(value.map(row => row.value as JobFunction));
                                                dirtyTracker.dirty();
                                            }}
                                            value={jobFunctions.map(row => ({
                                                label: JOB_FUNCTION_NAME[row],
                                                value: row,
                                            }))}
                                        />
                                        <Form.Text>
                                            What tags should be applied to this template?
                                        </Form.Text>
                                    </Form.Group>

                                </>
                            ) : (

                                <>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-bolder">
                                            Groups
                                        </Form.Label>
                                        <UserGroups
                                            isMulti
                                            canCreate
                                            value={userGroupIds}
                                            onSelected={ids => {
                                                setUserGroupIds(ids);
                                                dirtyTracker.dirty();
                                            }}
                                        />
                                        <Form.Text>
                                            Which user groups are allowed to access this request type?
                                        </Form.Text>
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-bolder">
                                            Always Private Create
                                        </Form.Label>
                                        <BooleanSelect
                                            isRequired
                                            value={isCreatePrivate}
                                            onChange={setIsCreatePrivate}
                                        />
                                        <Form.Text>
                                            When creating this request type, can the request be posted where visible
                                            by others, or should it always be private between the requester and the
                                            agent?
                                        </Form.Text>
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-bolder">
                                            Deflection Provider
                                        </Form.Label>
                                        <DeflectionSource
                                            value={deflectionIdentity}
                                            onSelected={setDeflectionIdentity}
                                        />
                                        <Form.Text>
                                            When enabled, Agent will attempt to use deflection to answer the users question.
                                            If not possible, or Agent can not find a good answer, a standard ticket will be
                                            created.
                                        </Form.Text>
                                    </Form.Group>
                                </>
                            )}
                            {dirtyTracker.saveComponent}
                        </form>

                        <div className="border p-3 rounded mb-4 bg-white">
                            <div className="d-flex justify-content-between w-100 align-items-center mb-2 ">
                                <h5 className="text-light mb-0">
                                    Automations
                                </h5>
                                <button
                                    type="button"
                                    onClick={() => setExpandAutomations(true)}
                                    className="btn btn-outline px-2 py-1 text-light p-1"
                                >
                                    <BiExpandAlt />
                                </button>
                            </div>

                            <WorkflowDisplay
                                shouldShowCreatingRequest
                                value={workflow}
                                onChange={updated => {
                                    setWorkflow(updated);
                                    dirtyTracker.dirty();
                                }}
                                input={createFormFields(formFields)}
                                isModal={isExpandedAutomation}
                                onHideModal={() => setExpandAutomations(false)}
                            />
                        </div>

                    </div>
                    <div className="d-flex flex-column col-4 ">

                        {!externalFormId && (
                            <div className="mb-4">
                                <FormDesigner
                                    fields={fields}
                                    formData={formData}
                                    formName={name}
                                    groupIds={userGroupIds}
                                    id={id}
                                    onCustomFieldAdded={refetch}
                                    isTemplate={isTemplate}
                                    setFields={value => {
                                        setFields(value);
                                        dirtyTracker.dirty();
                                    }}
                                />
                            </div>
                        )}
                        {!isTemplate && (

                            <div className="border p-3 rounded bg-white">
                                <strong>External Forms</strong>
                                <ExternalForms
                                    isClearable
                                    value={externalFormId}
                                    onSelected={value => {
                                        setExternalFormId(value);
                                    }}
                                />
                                <small className="text-gray-600">
                                    If you are using an integration that supports forms, attach the external form to this
                                    request type instead of creating the form in Agent directly.
                                </small>
                            </div>
                        )}
                        {!isNew && (
                            <div className="border p-3 mt-3 rounded bg-white align-items-center d-flex justify-content-between">
                                <strong className="d-block ">Actions</strong>
                                <div className="d-flex">
                                    <button
                                        type="button"
                                        className="d-block btn btn-none text-gray-500 py-0"
                                        onClick={() => setDisplayClone(true)}
                                    >
                                        <FaRegClone />
                                        {" "}
                                        Clone
                                    </button>

                                    <button
                                        type="button"
                                        className="d-block btn btn-none text-gray-500  py-0 pe-0"
                                        onClick={() => setDisplayDelete(true)}
                                    >
                                        <FiTrash2 />
                                        {" "}
                                        Delete
                                    </button>
                                </div>

                            </div>

                        )}

                    </div>

                </div>

            </div>
        </>
    );
}
